// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Shipment, getShipmentType } from '@/shipment/api/types';

// Mirrors the backend shipment gate: an action the server would refuse is never offered.

/** Why an edit form locks a field once the shipment is cancelled. */
export const CANCELLED_LOCK_REASON =
  'A cancelled shipment can only change its notes and shipping cost';

/** Why an edit form locks the order reference on a shipment a provider holds. */
export const MANAGED_ORDER_LOCK_REASON =
  'Your third-party logistics company manages this shipment';

/** Why an order reference a 3PL manages is refused. */
export const THIRD_PARTY_ORDER_MESSAGES = {
  purchaseOrder:
    'This PO is linked to a 3PL, shipments cannot be assigned manually',
  returnOrder:
    'This RO is linked to a 3PL, shipments cannot be assigned manually',
  fulfillmentOrder:
    'This FO is linked to a 3PL, shipments cannot be assigned manually',
};

/** The state the rules read: milestone timestamps, not the lossy status field. */
type ShipmentPhase = Pick<
  Shipment,
  | 'shipmentType'
  | 'returnOrder'
  | 'purchaseOrder'
  | 'fulfillmentOrder'
  | 'shippedAt'
  | 'deliveredAt'
  | 'receivedAt'
  | 'cancelledAt'
  | 'thirdPartyBound'
>;

/** Whether the shipment has committed stock: outbound at ship, inbound at receive. */
export function hasMovedInventory(shipment: ShipmentPhase): boolean {
  return getShipmentType(shipment) === 'inbound'
    ? shipment.receivedAt != null
    : shipment.shippedAt != null;
}

/** Whether the shipment is no longer open: shipped, delivered, or received. */
export function hasLeftOpen(shipment: ShipmentPhase): boolean {
  return (
    shipment.shippedAt != null ||
    shipment.deliveredAt != null ||
    shipment.receivedAt != null
  );
}

export function canMarkShipped(shipment: ShipmentPhase): boolean {
  return (
    getShipmentType(shipment) === 'outbound' &&
    shipment.cancelledAt == null &&
    shipment.shippedAt == null &&
    !shipment.thirdPartyBound
  );
}

/** Open on a bound shipment: the provider reports departure and never arrival. */
export function canMarkDelivered(shipment: ShipmentPhase): boolean {
  if (shipment.cancelledAt != null || shipment.deliveredAt != null) {
    return false;
  }
  return getShipmentType(shipment) === 'inbound' || shipment.shippedAt != null;
}

export function canMarkReceived(shipment: ShipmentPhase): boolean {
  return (
    getShipmentType(shipment) === 'inbound' &&
    shipment.cancelledAt == null &&
    shipment.deliveredAt != null &&
    shipment.receivedAt == null &&
    !shipment.thirdPartyBound
  );
}

/** Available until the shipment moves inventory, so inbound survives transit. */
export function canCancel(shipment: ShipmentPhase): boolean {
  return (
    shipment.cancelledAt == null &&
    !hasMovedInventory(shipment) &&
    !shipment.thirdPartyBound
  );
}

/** Adjusts a shipped outbound shipment; inbound correction is refused outright. */
export function canCorrectLineItemQuantity(shipment: ShipmentPhase): boolean {
  return (
    getShipmentType(shipment) === 'outbound' &&
    shipment.cancelledAt == null &&
    shipment.shippedAt != null &&
    !shipment.thirdPartyBound
  );
}

/** Whether the line items on the shipment can still be added to or removed. */
export function canEditManifest(shipment: ShipmentPhase): boolean {
  return (
    shipment.cancelledAt == null &&
    !hasMovedInventory(shipment) &&
    !shipment.thirdPartyBound
  );
}

/** Stricter than the gate on purpose: the dialogs' tables lock at ship. */
export function canEditManifestInForm(shipment: ShipmentPhase): boolean {
  return (
    canEditManifest(shipment) &&
    shipment.shippedAt == null &&
    shipment.deliveredAt == null &&
    shipment.receivedAt == null
  );
}
